import { FormControlLabel, Grid, Typography } from '@mui/material';
import { every, some } from 'lodash';
import React from 'react';
import { SortOptions } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { ControlType } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  StyledHeaderCheckbox,
  StyledRowsCheckbox,
} from 'src/features/locations/scheduleComponentPage/modules/additionalLocationGroups/AdditionalLocationGroups.styled';
import { lang } from 'src/utils';

export type LocationGroupRow = {
  id: number | string;
  name: string;
  locations_count: number;
  layoutType: string;
};

export type SelectedSort = { option: SortOptions; order: 'asc' | 'desc' };

type Props = {
  rows: LocationGroupRow[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  totalPages: number;
  selectedSortOption: SelectedSort;
  selectedLocationGroups: Set<LocationGroupRow['id']>;
  isAllSelected: boolean;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onSortChange: (next: SelectedSort) => void;
  onClearSelection: () => void;
  onSelectAll: (checked: boolean) => void;
  onSelectLocationGroup: (id: LocationGroupRow['id'], checked: boolean) => void;
  tableContainerHeight?: string;
};

export const AdditionalLocationGroupsTable: React.FC<Props> = ({
  rows,
  loading,
  page,
  rowsPerPage,
  totalCount,
  totalPages,
  selectedSortOption,
  selectedLocationGroups,
  isAllSelected,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onClearSelection,
  onSelectAll,
  onSelectLocationGroup,
  tableContainerHeight,
}) => {
  return (
    <CustomTableWithPagination
      columns={[
        { id: 'actions', label: lang.locationGroup, width: '27%' },
        {
          id: 'locations_count',
          label: lang.locationGroupTab.noOfLocations,
          width: '25%',
        },
        {
          id: 'layoutType',
          label: lang.locationGroupTab.channel,
          width: '25%',
        },
      ]}
      tableCellHeadProps={{
        sx: {
          fontSize: '16px',
        },
      }}
      onSortChange={onSortChange}
      rowsPerPage={rowsPerPage}
      selectedSortOption={selectedSortOption}
      rows={rows}
      loading={loading}
      page={page}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      totalCount={totalCount}
      totalPages={totalPages}
      showTableControls
      overrideHeight={totalPages > 1 ? '50vh' : '55vh'}
      visibleControls={[
        ControlType.SORT,
        ControlType.ITEMS_PER_PAGE,
        ControlType.CLEAR_SECTION,
      ]}
      onClearSelection={onClearSelection}
      selectedCount={selectedLocationGroups.size}
      renderActions={(row, col) => (
        <Grid container item wrap="nowrap" alignItems="center" sx={{ pl: 0 }}>
          <Grid item sx={{ marginRight: '0px' }}>
            <FormControlLabel
              control={
                col.id === 'actions' ? (
                  <StyledRowsCheckbox
                    checked={selectedLocationGroups.has(row.id)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation();
                      onSelectLocationGroup(row.id, e.target.checked);
                    }}
                  />
                ) : (
                  <></>
                )
              }
              label={''}
              sx={{ mr: 0 }}
            />
          </Grid>
          <Grid item>
            <CustomTooltip
              width={450}
              placement="bottom"
              title={row.name}
              overflowLineNumber={1}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.name}
              </Typography>
            </CustomTooltip>
          </Grid>
        </Grid>
      )}
      renderHeadActions={(col: any) => (
        <Grid container item wrap="nowrap" alignItems="center" sx={{ pl: 0 }}>
          <Grid item>
            <FormControlLabel
              control={
                <StyledHeaderCheckbox
                  checked={isAllSelected}
                  indeterminate={
                    some(rows, (r) => selectedLocationGroups.has(r.id)) &&
                    !every(rows, (r) => selectedLocationGroups.has(r.id)) &&
                    !isAllSelected
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.stopPropagation();
                    onSelectAll(e.target.checked);
                  }}
                />
              }
              label={
                <Grid container alignItems="center" spacing={0.5}>
                  <Grid item>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontSize: '16px',
                      }}>
                      {col.label}
                    </Typography>
                  </Grid>
                </Grid>
              }
              sx={{ mr: 2 }}
            />
          </Grid>
        </Grid>
      )}
      tableContainerHeight={tableContainerHeight}
    />
  );
};
